import { IS_IOS } from '@google/model-viewer/lib/constants';
import * as O from 'fp-ts/Option';
import * as TO from 'fp-ts/TaskOption';
import { pipe } from 'fp-ts/function';
import * as Hooks from 'preact/hooks';
import { P, match } from 'ts-pattern';
import * as Actions from '../../store/actions/app';
import * as Initialisation from '../../store/initialisation';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selectors from '../../store/selectors';
import * as ArState from './arState';
import * as Ios from './iOS';
import { useModelViewer } from './useModelViewer';
import { useTranslations } from '@dora/vivian';
import * as AR from '@dora/arctic';

/** The ways we can move from one AR State to another */
type UseArActions =
    | { tag: 'close_modal' }
    | { tag: 'open_ar_modal' }
    // separate from open_ar_modal as it indicates that the modal has been opened and the loading process has begun
    | { tag: 'ar_modal_loading_started' }
    | { tag: 'ar_modal_loaded'; launch: { text: string; action: () => void } }
    | { tag: 'delay_ar_loading' }
    | { tag: 'open_qr_modal' }
    | { tag: 'qr_modal_loaded'; url: string };

/**
 * Controls the current state of the AR modal
 */
const reducer = (
    /** Todo: validate current state before setting new state? The state is largely used as the output of the reducer right now */
    state: ArState.ArState,
    action: UseArActions,
): ArState.ArState => {
    const newState = match(action)
        .with({ tag: 'close_modal' }, () => ({
            tag: 'modal_closed',
        }))
        .with({ tag: 'open_ar_modal' }, () => ({ tag: 'ar_modal_loading', triggered: false }))
        .with({ tag: 'ar_modal_loading_started' }, () => ({
            tag: 'ar_modal_loading',
            triggered: true,
        }))
        .with({ tag: 'ar_modal_loaded', launch: P.select() }, launch => ({
            tag: 'ar_modal',
            launch,
        }))
        // sometimes we try to load the AR model too early - before the 3D scene is ready. This allows us to retry in that case
        .with({ tag: 'delay_ar_loading' }, () => ({
            tag: 'ar_delay_loading',
        }))
        .with({ tag: 'open_qr_modal' }, () => ({ tag: 'qr_modal_loading' }))
        .with({ tag: 'qr_modal_loaded', url: P.select() }, url => ({ tag: 'qr_modal', url }))
        .exhaustive() as ArState.ArState;

    return newState;
};

export type UseArOptions = {
    /** Callback to be triggered when the AR checkout button is pressed */
    onCheckout?: () => void;
    delayLoad: boolean;
    /** Generator function to produce a GLTF file blob based on the current contents of the 3D Scene */
    getModelUrl: TO.TaskOption<string>;
    usdzModelUrl?: string;
    generateQrUrl: TO.TaskOption<string>;
    placement: AR.Placement;
    isAppLaunching: boolean;
};

export function useAr(opts: UseArOptions) {
    const shouldOpenArModal = useSelector(Selectors.Initialisation.shouldShowArModal);
    const [modelUrl, setModelUrl] = Hooks.useState<Promise<O.Option<string>>>(TO.none);

    // if we end up in our delay state - for example if the 3D scene is not ready - how long do we delay before a retry?
    // this is required across multiple of our states so we store it here so we don't have to pass it around
    const [backoffDelay, setBackoffDelay] = Hooks.useState(500);

    const dispatch = useDispatch();

    const [arState, dispatchArState] = Hooks.useReducer(reducer, { tag: 'modal_closed' });

    const { t } = useTranslations();

    const modelViewer = useModelViewer(modelUrl, opts.placement);

    const iOSArElement = Hooks.useMemo(() => Ios.bootstrap(), []);

    Hooks.useEffect(() => {
        if (shouldOpenArModal) {
            dispatchArState({ tag: 'open_ar_modal' });
            dispatch(Actions.openArDrawerAction());
        }
    }, [shouldOpenArModal]);

    const checkUSDZModelExists = async (modelUrlOption?: string): Promise<boolean> => {
        if (!modelUrlOption) return false;

        try {
            const response = await fetch(modelUrlOption, { method: 'HEAD' });
            return response.ok;
        } catch (error) {
            console.error('Error checking USDZ model:', error);
            return false;
        }
    };

    const launchAr = async () => {
        dispatch(Actions.viewInArAction());
        /** set the state to loading to avoid the user feeling like nothing is happening */
        dispatchArState({ tag: 'ar_modal_loading_started' });

        const usdzExists = await checkUSDZModelExists(opts.usdzModelUrl);

        if (IS_IOS) {
            if (usdzExists && opts.usdzModelUrl) {
                try {
                    const response = await fetch(opts.usdzModelUrl);
                    const blob = await response.blob();
                    const fixedBlob = new Blob([blob], { type: 'model/vnd.usdz+zip' });
                    const objectUrl = URL.createObjectURL(fixedBlob);
                    const usdzModelUrl = new URL(objectUrl, self.location.toString());

                    Ios.loadAR(usdzModelUrl.toString(), iOSArElement, opts.onCheckout);

                    // clean up the object URL after use
                    URL.revokeObjectURL(objectUrl);
                } catch (error) {
                    console.error('Error downloading USDZ model:', error);
                }
                dispatchArState({ tag: 'close_modal' });
            } else {
                modelViewer.generateUSDZ().then(objectUrl => {
                    const modelUrl = new URL(objectUrl, self.location.toString());
                    Ios.loadAR(modelUrl.toString(), iOSArElement, opts.onCheckout);
                    // clean up the object URL after use
                    URL.revokeObjectURL(objectUrl);
                    dispatchArState({
                        tag: 'close_modal',
                    });
                });
            }
        } else {
            modelViewer.activate();
            dispatchArState({
                tag: 'close_modal',
            });
        }
    };

    // Load Data Required for AR Modal Loading
    Hooks.useEffect(() => {
        if (
            arState.tag === 'ar_modal_loading' &&
            !modelViewer.loading &&
            arState.triggered === true
        ) {
            dispatchArState({
                tag: 'ar_modal_loaded',
                launch: { text: t('ar.launch.button'), action: launchAr },
            });
        } else if (
            arState.tag === 'ar_modal_loading' &&
            arState.triggered === false &&
            opts.isAppLaunching === false
        ) {
            setModelUrl(() => {
                let delay = Promise.resolve();

                // this should only be set if we're opening up from a QR code directly. In that case, we need to give the model a little time to load
                // in an ideal world, we'd know when the loading was complete directly so we wouldn't need this - but there's more work that needs
                // to be done around how our loading state works.
                if (opts.delayLoad) {
                    delay = new Promise(res => {
                        setTimeout(res, 750);
                    });
                }

                return delay
                    .then(() => opts.getModelUrl())
                    .then(x => {
                        if (O.isNone(x)) {
                            // if we don't have an AR model yet, that suggest we're trying this too early - delay and then try again
                            dispatchArState({ tag: 'delay_ar_loading' });
                        }

                        return x;
                    });
            });
            dispatchArState({ tag: 'ar_modal_loading_started' });
        }
    }, [arState, modelViewer.loading, opts.isAppLaunching, opts.getModelUrl, opts.delayLoad]);

    // Retrying the loading if we're in a delay state
    Hooks.useEffect(() => {
        let timeout: number | undefined;
        if (arState.tag === 'ar_delay_loading') {
            timeout = window.setTimeout(() => {
                // attempt to start the process from scratch again
                dispatchArState({ tag: 'open_ar_modal' });
                // increase the delay such that it doubles each time
                setBackoffDelay(delay => delay * 2);
            }, backoffDelay);
        }

        return () => {
            if (timeout !== undefined) {
                clearTimeout(timeout);
            }
        };
    }, [arState]);

    return {
        openArModal: () => {
            dispatch(Actions.openArDrawerAction());
            if (modelViewer.canActivateAr) {
                // we have to do this here - if we did it in the part which handles the Ar modal opening, then it would always be reset even if we don't want it to be
                setBackoffDelay(500);
                dispatchArState({ tag: 'open_ar_modal' });
            } else {
                dispatchArState({ tag: 'open_qr_modal' });
                pipe(
                    opts.generateQrUrl,
                    TO.map(url => dispatchArState({ tag: 'qr_modal_loaded', url: url })),
                )();
            }
        },
        closeArModal: () => {
            if (ArState.isOpen(arState)) {
                dispatchArState({ tag: 'close_modal' });
                dispatch(Initialisation.resetShowArModal());
                dispatch(Actions.closeArDrawerAction());
            }
        },
        arState,
    };
}
